import React, { useState } from "react";
import {
    Trash2, ShieldAlert, Loader2, AlertTriangle, RefreshCw, CheckCircle2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Data purge — surgical test-data cleanup, platform_admin only.
 *
 * Backend refuses over-broad patterns (< 4 chars, missing '@' or '.') and
 * runs dry-run TRUE by default. This UI mirrors both guardrails and adds
 * a type-CONFIRM gate before letting the operator flip dry_run to false.
 */
export default function DataPurge() {
    const [pattern, setPattern] = useState("@example.com");
    const [plan, setPlan] = useState(null);
    const [busy, setBusy] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [committed, setCommitted] = useState(null);

    const dryRun = async () => {
        if (!pattern.trim()) return;
        setBusy(true);
        setCommitted(null);
        try {
            const { data } = await api.post("/admin/dev/purge-test-data", {
                email_pattern: pattern.trim(),
                dry_run: true,
            });
            setPlan(data);
            setConfirmText("");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const commit = async () => {
        if (confirmText.trim() !== "CONFIRM") return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/dev/purge-test-data", {
                email_pattern: pattern.trim(),
                dry_run: false,
            });
            setCommitted(data);
            setPlan(null);
            setConfirmText("");
            toast.success(`Deleted ${data.total_deleted} row(s).`);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-data-purge">
            <div className="mb-6">
                <p className="overline">Compliance · Data hygiene</p>
                <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Test Data Purge</h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    Surgical email-pattern purge for cleaning synthetic test seeds. Backend refuses patterns
                    shorter than 4 chars or missing <code className="font-mono">@</code>/<code className="font-mono">.</code>.
                    Always run <strong>Preview (dry-run)</strong> first — the commit step requires typing CONFIRM.
                </p>
            </div>

            <div className="rounded-2xl border border-amber-500/40 bg-amber-500/[0.06] p-4 mb-6 flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                    <strong>Live production is Stripe-live and has real employer accounts.</strong> Only use this to
                    clean documented synthetic seeds (e.g. <code className="font-mono">@example.com</code>). Every run is audited.
                </div>
            </div>

            <div className="card-soft p-5 space-y-3">
                <label className="block">
                    <span className="text-xs font-semibold text-foreground">Email substring to match (case-insensitive)</span>
                    <Input
                        data-testid="purge-pattern-input"
                        value={pattern}
                        onChange={(e) => setPattern(e.target.value)}
                        placeholder="@example.com"
                        className="mt-1 font-mono"
                    />
                    <span className="text-xs text-muted-foreground mt-1 block">
                        Applies to <code className="font-mono">email</code>, <code className="font-mono">candidate_email</code>, and shaped snapshot keys across the allow-listed collections.
                    </span>
                </label>
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={dryRun}
                        disabled={busy || !pattern.trim()}
                        data-testid="purge-dryrun-btn"
                        className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-white dark:bg-transparent text-emerald-700 dark:text-emerald-400 px-4 py-1.5 text-sm font-semibold hover:bg-emerald-500/10 disabled:opacity-60"
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Preview (dry-run)
                    </button>
                </div>
            </div>

            {plan && (
                <div data-testid="purge-plan" className="card-soft p-5 mt-6 space-y-3">
                    <div className="flex items-center gap-2">
                        <AlertTriangle size={16} className="text-amber-500" />
                        <h3 className="font-heading font-semibold text-foreground">Dry-run result</h3>
                    </div>
                    <div className="text-sm text-muted-foreground">
                        Pattern <code className="font-mono">{plan.pattern}</code> would delete
                        <strong data-testid="purge-plan-total" className="text-foreground"> {plan.total_targeted}</strong> row(s) across these collections:
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left border-b border-border">
                                <th className="py-1.5 font-semibold text-muted-foreground">Collection</th>
                                <th className="py-1.5 font-semibold text-muted-foreground text-right">Rows to delete</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(plan.plan).map(([c, n]) => (
                                <tr key={c} data-testid={`purge-plan-row-${c}`} className="border-b border-border/50">
                                    <td className="py-1.5 font-mono text-xs">{c}</td>
                                    <td className={`py-1.5 text-right tabular-nums ${n > 0 ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>{n}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {plan.total_targeted > 0 ? (
                        <div className="pt-3 border-t border-border space-y-2">
                            <div className="text-sm text-foreground">
                                Type <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">CONFIRM</code> to run the actual delete. This is <strong>irreversible</strong>.
                            </div>
                            <Input
                                data-testid="purge-confirm-input"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder="CONFIRM"
                                className="font-mono"
                            />
                            <button
                                onClick={commit}
                                disabled={busy || confirmText.trim() !== "CONFIRM"}
                                data-testid="purge-commit-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-rose-600 text-white px-4 py-1.5 text-sm font-semibold hover:bg-rose-700 disabled:opacity-60"
                            >
                                {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete {plan.total_targeted} rows permanently
                            </button>
                        </div>
                    ) : (
                        <div className="text-sm text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1">
                            <CheckCircle2 size={13} /> Nothing to delete — this pattern matches no rows.
                        </div>
                    )}
                </div>
            )}

            {committed && (
                <div data-testid="purge-committed" className="card-soft border-emerald-500/40 bg-emerald-500/[0.05] p-5 mt-6 space-y-2">
                    <div className="flex items-center gap-2">
                        <CheckCircle2 size={16} className="text-emerald-600" />
                        <h3 className="font-heading font-semibold text-foreground">Purge complete</h3>
                    </div>
                    <div className="text-sm text-foreground">
                        Deleted <strong data-testid="purge-committed-total">{committed.total_deleted}</strong> row(s) for pattern <code className="font-mono">{committed.pattern}</code>.
                    </div>
                    <table className="w-full text-sm">
                        <tbody>
                            {Object.entries(committed.results || {}).map(([c, n]) => (
                                <tr key={c} className="border-b border-border/50">
                                    <td className="py-1.5 font-mono text-xs">{c}</td>
                                    <td className={`py-1.5 text-right tabular-nums ${n > 0 ? "text-emerald-700 dark:text-emerald-400 font-semibold" : "text-muted-foreground"}`}>{n}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
